import { createRouter, createWebHistory } from 'vue-router';
import axios from 'axios';
import authService from '@/services/auth.js';
import logger from '@/services/logger';
import { hqApi, referralApi } from '@/services/consoleApi';
import { publicRoutes } from './routes/publicRoutes';
import { researchRoutes } from './routes/researchRoutes';
import { accountRoutes } from './routes/accountRoutes';

const router = createRouter({
  history: createWebHistory(),
  routes: [...publicRoutes, ...researchRoutes, ...accountRoutes],
  scrollBehavior(to, from, savedPosition) {
    return savedPosition || { top: 0, left: 0 };
  },
});

axios.interceptors.response.use(
  response => response,
  async error => {
    const requestConfig = error.config || {};
    const status = error.response?.status;
    const isRetryableGet = String(requestConfig.method || 'get').toLowerCase() === 'get'
      && (!status || status >= 500)
      && !requestConfig.__paperdocRetried;
    if (isRetryableGet) {
      requestConfig.__paperdocRetried = true;
      await new Promise(resolve => window.setTimeout(resolve, 250));
      return axios(requestConfig);
    }
    if (status === 401 || (status === 403 && error.response?.data?.reason_code === 'ACCOUNT_DISABLED')) {
      authService.removeToken();
      router.push(status === 401 ? '/login' : '/login?reason=disabled');
    } else if (status === 402) {
      try {
        const redirectPath = await authService.handle402Error(error);
        if (redirectPath) {
          logger.log('router', `402 error handled, redirecting to: ${redirectPath}`);
          router.push(redirectPath);
        }
      } catch (handlerError) {
        logger.error('router', 'Error handling 402 response:', handlerError);
        router.push('/projects');
      }
    }
    return Promise.reject(error);
  }
);

router.beforeEach((to) => {
  // The referral center and the HQ console keep their own sessions, separate from website users.
  if (to.name === 'ReferralCenter') return referralApi.hasSession() ? true : '/referral/login';
  if (to.name === 'HqConsole') return hqApi.hasSession() ? true : '/hq/login';
  if (to.name === 'ReferralLogin' && referralApi.hasSession()) return '/referral';
  if (to.name === 'HqLogin' && hqApi.hasSession()) return '/hq';
  if (to.path.startsWith('/referral') || to.path.startsWith('/hq')) return true;
  if (authService.shouldShowAuthUI(to.path) && authService.isAuthenticated()) {
    const redirect = typeof to.query.redirect === 'string'
      && to.query.redirect.startsWith('/')
      && !to.query.redirect.startsWith('//')
      ? to.query.redirect
      : '/projects';
    return redirect;
  }
  if (authService.requiresAuth(to.path) && !authService.isAuthenticated()) {
    return { path: '/login', query: { redirect: to.fullPath } };
  }
  return true;
});

export default router;
